import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { INPUT_METHOD, INSERT_MEDIA_VIA } from '@atlaskit/editor-common/analytics';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { toolbarInsertBlockMessages as messages } from '@atlaskit/editor-common/messages';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import {
	PlainOutsideClickTargetRefContext,
	Popup,
	withOuterListeners,
} from '@atlaskit/editor-common/ui';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { akEditorFloatingDialogZIndex } from '@atlaskit/editor-shared-styles';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { ImageFromAIGeneration } from '@atlassian/ai-image-generation/image-from-ai-generation';

import type { AIImageGenerationPlugin } from '../aiImageGenerationPluginType';
import { closeAIImageGenerationPopup } from '../pm-plugins/actions';
import { AI_IMAGE_GENERATION_ANCHOR_ATTR } from '../pm-plugins/main';

const PopupWithListeners = withOuterListeners(Popup);

const styles = cssMap({
	wrapper: {
		whiteSpace: 'initial',
		textAlign: 'initial',
		boxShadow: token('elevation.shadow.overflow'),
		width: '340px',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		borderRadius: token('radius.small'),
		backgroundColor: token('elevation.surface.overlay'),
	},
});

type AIImageGenerationPopupProps = {
	api: ExtractInjectionAPI<AIImageGenerationPlugin> | undefined;
	editorView: EditorView;
	popupsBoundariesElement?: HTMLElement;
	popupsMountPoint?: HTMLElement;
	popupsScrollableElement?: HTMLElement;
};

export function AIImageGenerationPopup({
	api,
	editorView,
	popupsMountPoint,
	popupsBoundariesElement,
	popupsScrollableElement,
}: AIImageGenerationPopupProps): JSX.Element | null {
	const { formatMessage } = useIntl();
	const { mediaProvider, isOpen } = useSharedPluginStateWithSelector(
		api,
		['media', 'aiImageGeneration'],
		(states) => ({
			mediaProvider: states.mediaState?.mediaProvider,
			isOpen: states.aiImageGenerationState?.isOpen,
		}),
	);

	const closePopup = useCallback(() => {
		editorView.dispatch(closeAIImageGenerationPopup(editorView.state.tr));
		editorView.focus();
	}, [editorView]);

	const handleClickOutside = useCallback((event: Event) => {
		// When clicking a Select option, react-select removes the clicked
		// element from the DOM before this handler fires. The detached node
		// is no longer contained in the popup wrapper, so withOuterListeners
		// incorrectly treats it as an outside click. Skip closing when the
		// target has been disconnected from the document.
		const target = event.target as Node | null;
		if (target && !target.isConnected) {
			return;
		}
		event.preventDefault();
		closePopup();
	}, [closePopup]);

	const handleEscapeKeydown = useCallback((event: Event) => {
		event.preventDefault();
		closePopup();
	}, [closePopup]);

	const insertMediaSingle = useCallback(
		({
			id,
			collection,
			fileMimeType,
			dimensions,
		}: {
			collection: string | undefined;
			dimensions?: { height: number; width: number; };
			fileMimeType?: string;
			id: string | undefined;
		}) => {
			const { width, height } = dimensions || { height: undefined, width: undefined };

			const node = editorView.state.schema.nodes.media.create({
				id,
				type: 'file',
				collection,
				width,
				height,
				__fileMimeType: fileMimeType,
			});

			api?.media.actions.insertMediaAsMediaSingle(
				editorView,
				node,
				INPUT_METHOD.MEDIA_PICKER,
				INSERT_MEDIA_VIA.EXTERNAL_UPLOAD,
			);
		},
		[api, editorView],
	);

	if (!isOpen || !mediaProvider) {
		return null;
	}

	// Anchor to the decoration widget that the PM plugin mounts at the caret
	// when the popup opens. The widget is an inline zero-width DOM node, so its
	// bounding rect is aligned with the caret even in center/right-aligned
	// paragraphs, layout columns, table cells, etc.
	const targetRef = editorView.dom.querySelector<HTMLElement>(
		`[${AI_IMAGE_GENERATION_ANCHOR_ATTR}]`,
	);

	// Without a real anchor element Popper would position against the viewport
	// origin. Bail out instead of rendering a mis-placed popup — the next
	// transaction that mounts the widget (or closes the plugin) will re-render.
	if (!targetRef) {
		return null;
	}

	return (
		<PopupWithListeners
			ariaLabel={formatMessage(messages.aiImageGenerationPopupAriaLabel)}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			offset={[0, 12]}
			target={targetRef}
			zIndex={akEditorFloatingDialogZIndex}
			fitHeight={390}
			fitWidth={340}
			mountTo={popupsMountPoint}
			boundariesElement={popupsBoundariesElement}
			handleClickOutside={handleClickOutside}
			handleEscapeKeydown={handleEscapeKeydown}
			scrollableElement={popupsScrollableElement}
			preventOverflow={true}
			focusTrap
		>
			<PlainOutsideClickTargetRefContext.Consumer>
				{(setOutsideClickTargetRef) => (
					<Box ref={setOutsideClickTargetRef} xcss={styles.wrapper}>
						<ImageFromAIGeneration
							mediaUploadCollection={mediaProvider.uploadParams?.collection}
							insertMedia={insertMediaSingle}
							closePopup={closePopup}
						/>
					</Box>
				)}
			</PlainOutsideClickTargetRefContext.Consumer>
		</PopupWithListeners>
	);
}
